/**
 * Belge düzeyinde metin indeksi.
 *
 * Motorlar (stats, citations, structure, language) tek bir normalize metin
 * üzerinde çalışır. Bu indeks, metindeki offset'leri sayfa numarasına ve
 * bounding box'a geri çözer; "geometrik bilgi saklama" gereksiniminin
 * merkezindedir.
 */

import type { ExtractedDocument, IssueLocation, NormRect, TextBlock, TextLine } from '../types';
import { toRawOffset } from './normalize';

export class DocumentIndex {
	readonly doc: ExtractedDocument;
	private readonly blockRanges: { start: number; end: number; block: TextBlock }[];
	private readonly starts = new Map<TextBlock, number>();

	constructor(doc: ExtractedDocument) {
		this.doc = doc;
		this.blockRanges = [];
		let cursor = 0;
		for (const block of doc.blocks) {
			const start = cursor;
			this.starts.set(block, start);
			cursor += block.text.length + 2; // \n\n
			this.blockRanges.push({ start, end: cursor, block });
		}
	}

	get text(): string {
		return this.doc.text;
	}

	blockAt(offset: number): TextBlock | undefined {
		let lo = 0;
		let hi = this.blockRanges.length - 1;
		while (lo <= hi) {
			const mid = (lo + hi) >> 1;
			const r = this.blockRanges[mid];
			if (offset < r.start) hi = mid - 1;
			else if (offset >= r.end) lo = mid + 1;
			else return r.block;
		}
		return this.blockRanges[Math.min(lo, this.blockRanges.length - 1)]?.block;
	}

	/** Normalize offset aralığının ham (kaynak) metnini döndürür. */
	rawAt(start: number, end: number): string {
		const block = this.blockAt(start);
		if (!block) return '';
		const blockStart = this.starts.get(block) ?? 0;
		const localStart = Math.max(0, Math.min(block.text.length, start - blockStart));
		const localEnd = Math.max(localStart, Math.min(block.text.length, end - blockStart));
		if (!block.offsetMap) return block.raw.slice(localStart, localEnd);
		const rawStart = toRawOffset(block.offsetMap, localStart);
		const rawEnd = localEnd > localStart ? toRawOffset(block.offsetMap, localEnd - 1) + 1 : rawStart;
		return block.raw.slice(rawStart, Math.max(rawStart, rawEnd));
	}

	/** Verilen aralığın konumunu çözer. */
	locationAt(start: number, end: number): IssueLocation {
		const block = this.blockAt(start);
		if (!block) return {};
		const blockStart = this.starts.get(block) ?? 0;
		const localStart = Math.max(0, start - blockStart);
		const localEnd = Math.min(block.text.length, Math.max(localStart, end - blockStart));

		const lineBegin = Math.max(0, block.text.lastIndexOf('\n', localStart) + 1);
		const nl = block.text.indexOf('\n', localEnd);
		const lineEnd = nl === -1 ? block.text.length : nl;

		const loc: IssueLocation = {
			page: block.page,
			blockId: block.id,
			charStart: localStart,
			charEnd: localEnd,
			// Blok içindeki satır sayısını ekleyerek tam kaynak satırını bul
			sourceLine:
				block.lineStart !== undefined
					? block.lineStart + countNewlines(block.text, localStart)
					: undefined,
			line: block.text.slice(lineBegin, lineEnd)
		};
		const rect = this.rectAt(block, localStart, localEnd);
		if (rect) loc.rect = rect;
		return loc;
	}

	/**
	 * Blok içi bir aralığın normalize koordinatlarını hesaplar.
	 * PDF satırları için lineer interpolasyon; DOCX/LaTeX için yoktur.
	 */
	rectAt(block: TextBlock, localStart: number, localEnd: number): NormRect | undefined {
		if (!block.lines || block.lines.length === 0) return undefined;
		const lineIdx = lineIndexAt(block, localStart);
		if (lineIdx === -1) return undefined;
		const line = block.lines[lineIdx];
		if (!line.pageWidth || !line.pageHeight) return undefined;

		const lineText = line.text;
		if (lineText.length === 0) return undefined;

		const offsets = block.lineOffsets;
		let lineStartInBlock: number;
		if (offsets && offsets.length > lineIdx) {
			lineStartInBlock = offsets[lineIdx];
		} else {
			let cursor = 0;
			for (let k = 0; k < lineIdx; k++) {
				const idx = block.text.indexOf(block.lines[k].text, cursor);
				cursor = idx === -1 ? cursor : idx + block.lines[k].text.length;
			}
			lineStartInBlock = cursor;
		}

		const charStart = clamp(localStart - lineStartInBlock, 0, lineText.length);
		const charEnd = clamp(localEnd - lineStartInBlock, charStart, lineText.length);
		if (charEnd <= charStart) return undefined;

		const x0 = line.x + (line.width * charStart) / lineText.length;
		const x1 = line.x + (line.width * charEnd) / lineText.length;
		const yTop = line.pageHeight - (line.y + line.height);
		return {
			x: clamp(x0 / line.pageWidth, 0, 1),
			y: clamp(yTop / line.pageHeight, 0, 1),
			w: clamp((x1 - x0) / line.pageWidth, 0, 1),
			h: clamp(line.height / line.pageHeight, 0, 1)
		};
	}

	/** Sayfa geometrisi (önizleme için). */
	pageLines(page: number): TextLine[] {
		const lines: TextLine[] = [];
		for (const block of this.doc.blocks) {
			if (block.page === page && block.lines) lines.push(...block.lines);
		}
		return lines.sort((a, b) => b.y - a.y || a.x - b.x);
	}

	/** Metindeki bir aralığı orijinal (ham) metne geri çevirir. */
	rawRange(block: TextBlock, localStart: number, localEnd: number): [number, number] {
		if (!block.offsetMap) return [localStart, localEnd];
		return [toRawOffset(block.offsetMap, localStart), toRawOffset(block.offsetMap, localEnd)];
	}
}

function lineIndexAt(block: TextBlock, localOffset: number): number {
	if (!block.lines) return -1;
	const offsets = block.lineOffsets;
	if (offsets && offsets.length === block.lines.length) {
		let lo = 0;
		let hi = offsets.length - 1;
		let ans = -1;
		while (lo <= hi) {
			const mid = (lo + hi) >> 1;
			if (offsets[mid] <= localOffset) {
				ans = mid;
				lo = mid + 1;
			} else {
				hi = mid - 1;
			}
		}
		return ans;
	}
	let cursor = 0;
	for (let i = 0; i < block.lines.length; i++) {
		const t = block.lines[i].text;
		const idx = block.text.indexOf(t, cursor);
		if (idx === -1) continue;
		if (localOffset >= idx && localOffset < idx + t.length) return i;
		cursor = idx + t.length;
	}
	return -1;
}

function clamp(v: number, lo: number, hi: number): number {
	return Math.max(lo, Math.min(hi, v));
}

/** [0, end) aralığındaki satır sonu sayısı. */
function countNewlines(text: string, end: number): number {
	let n = 0;
	for (let i = 0; i < end && i < text.length; i++) {
		if (text.charCodeAt(i) === 10) n++;
	}
	return n;
}
